/**
 * Queue persistence layer.
 *
 * Wraps all localStorage interactions in try/catch so that environments
 * where storage is blocked (private browsing, storage-full, CSP) degrade
 * gracefully rather than crashing the app.
 */

import { STORAGE_KEY } from "./constants";

/**
 * Read the persisted event queue from localStorage.
 *
 * @returns {Array<Object>} Restored events, or an empty array if nothing is stored
 *                          or the stored value cannot be parsed.
 */
export function loadQueueFromStorage() {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) {
			return [];
		}

		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		// JSON parse error or localStorage access denied — start with an empty queue.
		return [];
	}
}

/**
 * Persist the current in-memory queue to localStorage.
 *
 * @param {Array<Object>} queue - Full queue snapshot to persist.
 */
export function saveQueueToStorage(queue) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
	} catch (err) {
		// QuotaExceededError or SecurityError — log and continue; data may be lost on refresh.
		console.warn("[MoEngageQueue] Could not persist queue to storage:", err);
	}
}

/**
 * Remove the persisted queue from localStorage.
 * Called after a successful full-flush or an explicit clearQueue().
 */
export function clearQueueStorage() {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Silently ignore — removal failing has no functional impact.
	}
}

/**
 * Check whether localStorage is actually usable in this environment.
 * Some browsers throw even on a simple setItem test when storage is blocked.
 *
 * @returns {boolean}
 */
export function isStorageAvailable() {
	try {
		const testKey = "__moeq_test__";
		localStorage.setItem(testKey, "1");
		localStorage.removeItem(testKey);
		return true;
	} catch {
		return false;
	}
}
